import { ClipboardModule } from '@angular/cdk/clipboard';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { FileUploadModule } from '@vectorsolv/vectors-ui/file-input';
import { AddressModule } from '@vsolv/core/address/web';
import { PaymentMethodModule } from '@vsolv/packages/payments/web';
import { AddDocumentDialog, EditDocumentDialog } from '@vsolv/packages/storage/web';
import { ContentCardModule } from '@vsolv/ui-kit/card';
import { ModalModule } from '@vsolv/ui-kit/modal';
import { UtilsModule } from '@vsolv/ui-kit/utils';
import { AlertModule } from '@vsolv/vectors-ui/alert';
import { AppNavModule } from '@vsolv/vectors-ui/app-nav';
import { AppPageModule } from '@vsolv/vectors-ui/app-page';
import { AppScaffoldModule } from '@vsolv/vectors-ui/app-scaffold';
import { BadgeModule } from '@vsolv/vectors-ui/badge';
import { BreadcrumbsModule } from '@vsolv/vectors-ui/breadcrumbs';
import { ButtonModule } from '@vsolv/vectors-ui/button';
import { CardModule } from '@vsolv/vectors-ui/card';
import { DatePickerModule } from '@vsolv/vectors-ui/date-picker';
import { DialogModule } from '@vsolv/vectors-ui/dialog';
import { DividerModule } from '@vsolv/vectors-ui/divider';
import { DropdownModule } from '@vsolv/vectors-ui/dropdown';
import { FormsModule } from '@vsolv/vectors-ui/forms';
import { IconsModule } from '@vsolv/vectors-ui/icons';
import { InputModule } from '@vsolv/vectors-ui/input';
import { MenuModule } from '@vsolv/vectors-ui/menu';
import { ProgressBarModule } from '@vsolv/vectors-ui/progress-bar';
import { RichTextEditorModule } from '@vsolv/vectors-ui/rich-text-editor';
import { SelectModule } from '@vsolv/vectors-ui/select';
import { SkeletonModule } from '@vsolv/vectors-ui/skeleton';
import { SlideOutModule } from '@vsolv/vectors-ui/slide-out';
import { StepperModule } from '@vsolv/vectors-ui/stepper';
import { TabsModule } from '@vsolv/vectors-ui/tabs';
import { ThemingModule } from '@vsolv/vectors-ui/theming';
import { ToolbarModule } from '@vsolv/vectors-ui/toolbar';
import { TooltipModule } from '@vsolv/vectors-ui/tooltip';
import { UserAvatarModule } from '@vsolv/vectors-ui/user-avatar';
import { ConnectedTableComponent, TableModule } from '@vsolv/vectors/table';
import { CustomerWebModule } from '@wsphere/customers/web';
import { SftpWebModule } from '@wsphere/sftp/web';
import { StaffWebModule } from '@wsphere/staff/web';
import { NgPipesModule } from 'ngx-pipes';
import {
  CheckboxCellComponent,
  CommissionPolicyPlanPickerComponent,
  DistributorDetailsFormComponent,
  DistributorNameCellComponent,
  DistributorOnboardCompleteComponent,
  DistributorPickerComponent,
  DistributorTableComponent,
  SettlementStatementPDFComponent,
} from './components';
import {
  ArchiveDistributorDialog,
  BreakDistributorOnboardingLinkDialog,
  CreateDistributorDialog,
  CreateDistributorOnboardingLinkDialog,
  DeactivateDistributorDialog,
  InviteMemberDialog,
  PayBatchDialog,
  PaymentHistoryDetailDialog,
  ReactivateDistributorDialog,
  RecordSellerPayoutDialog,
} from './dialogs';
import { DistributorGuard } from './guards';
import {
  DistributorAccountPage,
  DistributorCommissionPage,
  DistributorLedgerStatementPage,
  DistributorNotesPage,
  DistributorOverviewPage,
  DistributorPage,
  DistributorPaymentHistoryPage,
  DistributorSettingsPage,
  DistributorSFTPPage,
  DistributorsPage,
  DistributorTeamPage,
  DistributorUnfundedWarrantiesPage,
  ExpiredDistributorLinkPage,
  OnboardDistributorPage,
} from './pages';
import { DistributorDocumentsPage } from './pages/documents/documents.page';
import { NewNoteDialogComponent } from './pages/notes/new-note.dialog';
import { DistributorResolver, DistributorSettingsResolver } from './resolvers';
import {
  BatchPaymentService,
  CommissionService,
  DistributorService,
  LedgerStatementDownloadService,
  LedgerStatementService,
  PaymentHistoryService,
  UnfundedWarrantiesService,
} from './services';

@NgModule({
  imports: [
    CommonModule,
    ModalModule,
    ContentCardModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    UtilsModule,
    StaffWebModule,
    AppPageModule,
    BadgeModule,
    IconsModule,
    ButtonModule,
    ThemingModule,
    TableModule,
    DatePickerModule,
    DialogModule,
    DividerModule,
    AppNavModule,
    CardModule,
    BreadcrumbsModule,
    InputModule,
    FormsModule,
    SelectModule,
    RichTextEditorModule,
    MenuModule,
    ProgressBarModule,
    StepperModule,
    TooltipModule,
    AlertModule,
    IconsModule,
    TabsModule,
    CustomerWebModule,
    DropdownModule,
    BadgeModule,
    OverlayModule,
    AppScaffoldModule,
    ToolbarModule,
    SlideOutModule,
    UserAvatarModule,
    ClipboardModule,
    SftpWebModule,
    NgPipesModule,
    SkeletonModule,
    FileUploadModule,
    AddressModule,
    PaymentMethodModule,
    ConnectedTableComponent,
    AddDocumentDialog,
    EditDocumentDialog,
  ],
  providers: [
    DistributorService,
    CommissionService,
    UnfundedWarrantiesService,
    BatchPaymentService,
    PaymentHistoryService,
    LedgerStatementService,
    LedgerStatementDownloadService,
    DistributorResolver,
    DistributorGuard,
    DistributorSettingsResolver,
  ],
  declarations: [
    // Pages
    DistributorsPage,
    DistributorPage,
    DistributorOverviewPage,
    DistributorTeamPage,
    DistributorSFTPPage,
    OnboardDistributorPage,
    ExpiredDistributorLinkPage,
    DistributorSettingsPage,
    DistributorNotesPage,
    DistributorDocumentsPage,
    DistributorCommissionPage,
    DistributorAccountPage,
    DistributorUnfundedWarrantiesPage,
    DistributorPaymentHistoryPage,
    DistributorLedgerStatementPage,

    // Components
    DistributorPickerComponent,
    DistributorTableComponent,
    DistributorDetailsFormComponent,
    DistributorOnboardCompleteComponent,
    CommissionPolicyPlanPickerComponent,
    CheckboxCellComponent,
    DistributorNameCellComponent,
    SettlementStatementPDFComponent,

    // Dialogs
    InviteMemberDialog,
    CreateDistributorDialog,
    ArchiveDistributorDialog,
    CreateDistributorOnboardingLinkDialog,
    DeactivateDistributorDialog,
    ReactivateDistributorDialog,
    BreakDistributorOnboardingLinkDialog,
    DeactivateDistributorDialog,
    ReactivateDistributorDialog,
    NewNoteDialogComponent,
    PayBatchDialog,
    PaymentHistoryDetailDialog,
    RecordSellerPayoutDialog,
  ],
  exports: [
    // Pages
    DistributorsPage,
    DistributorPage,
    DistributorOverviewPage,
    DistributorTeamPage,
    DistributorSFTPPage,
    OnboardDistributorPage,
    ExpiredDistributorLinkPage,
    DistributorSettingsPage,
    DistributorDocumentsPage,
    DistributorCommissionPage,
    DistributorAccountPage,
    DistributorUnfundedWarrantiesPage,
    DistributorPaymentHistoryPage,
    DistributorLedgerStatementPage,

    // Components
    DistributorPickerComponent,
    DistributorTableComponent,
    DistributorDetailsFormComponent,
    DistributorOnboardCompleteComponent,
    CommissionPolicyPlanPickerComponent,

    // Dialogs
    InviteMemberDialog,
    CreateDistributorDialog,
    ArchiveDistributorDialog,
    DeactivateDistributorDialog,
    ReactivateDistributorDialog,
  ],
})
export class DistributorsWebModule {}
